import React, { useState, useEffect } from 'react'
import { useField, ArrayHelpers, useFormikContext } from 'formik'
import * as R from 'ramda'

// app components
import Button, { ButtonProps } from '../Button'

// type definition
export type AddOrRemovePassProps = {
    remove: boolean
    curValue: any
    entries: any[]
}

export type AddOrRemoveProps = {
    name: string
    index: number
    itemModel: any
    optionalProps?: Array<string>
    toRemove?: boolean
    onAction?: (props: AddOrRemovePassProps) => void
    actions?: Pick<ArrayHelpers, 'push' | 'remove'>
    children?: ((props: AddOrRemovePassProps) => React.ReactNode) | React.ReactNode
} & Omit<ButtonProps, 'onClick' | 'children'>

const AddOrRemove = React.memo<AddOrRemoveProps>(
    ({ name, index, itemModel, toRemove, optionalProps, onAction, actions, children, color, ...props }) => {
        // initial state
        const [remove, setRemove] = useState(!!toRemove)
        const meta = useField(name)[1]
        const { validateForm } = useFormikContext()
        const { initialValue, value: entries } = meta

        // declarations
        const initValue =
            Array.isArray(initialValue) && initialValue.length
                ? itemModel
                : Array.isArray(entries) && entries.length
                ? typeof entries[0] === 'object'
                    ? R.map(R.empty, entries[0])
                    : R.empty(entries[0])
                : initialValue | R.empty(entries)
        const addInitial = initValue

        useEffect(() => {
            if (index < entries.length - 1 && typeof toRemove === 'undefined') {
                setRemove(true)
            } else {
                setRemove(!!toRemove)
            }
        }, [entries.length, index, toRemove])

        // event handler that will trigger the action
        const handleAction = (event: React.MouseEvent<HTMLElement>): void => {
            event.preventDefault()

            validateForm(name).then(() => {
                if (typeof actions === 'object') {
                    const item = {...entries[index]}
                    if(optionalProps) {
                        optionalProps.forEach(prop => delete item[prop])
                    }

                    remove
                        ? actions.remove(index)
                        : (typeof item === 'object' && item !== null
                              ? Object.values(item).every(v =>
                                    typeof v !== 'boolean' ? !!v : true
                                )
                            : typeof item === 'boolean' || !!item) &&
                          actions.push(addInitial)
                }

                if (onAction) {
                    onAction({ curValue: entries[index], entries, remove })
                }
            })
        }

        return (
            <Button
                type="button"
                color={color ? color : remove ? 'danger' : undefined}
                onClick={handleAction}
                {...props}
            >
                {typeof children === 'function' && 'call' in children
                    ? children({ curValue: entries[index], entries, remove })
                    : children || (remove ? '-' : '+')}
            </Button>
        )
    },
    (prevProps, nextProps) => {
        return prevProps.index === nextProps.index && prevProps.toRemove === nextProps.toRemove
    }
)

AddOrRemove.displayName = 'AddOrRemove(memo)'

export default AddOrRemove
